import React, { useEffect } from 'react';
import { useParams, Navigate, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/axios';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';

export const ProjectDetail: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { data: project, isLoading, isError } = useQuery({
        queryKey: ['publicProject', slug],
        queryFn: async () => {
            const response = await apiClient.get(`/public/projects/${slug}`);
            return response.data.data;
        },
        enabled: !!slug,
        retry: false
    });

    useEffect(() => {
        if (project?.title) {
            document.title = `${project.title} | Mosaik Offenbach`;
        }
    }, [project?.title]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-32 min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (isError || !project) {
        return <Navigate to="/projekte" replace />;
    }

    const resolvedImageUrl = resolveAssetUrl(project.imageUrl);

    return (
        <div className="flex flex-col bg-background min-h-screen pb-16">
            <div
                className="h-64 sm:h-80 relative flex items-center justify-center text-center px-4"
                style={{
                    background: resolvedImageUrl
                        ? `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${resolvedImageUrl}) center/cover no-repeat`
                        : (project.color || 'var(--color-primary)')
                }}
            >
                <div>
                    <h1 className="text-3xl sm:text-5xl font-bold text-white mb-4">{project.title}</h1>
                </div>
            </div>

            <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 w-full -mt-8 relative z-10">
                <nav className="mb-6 text-sm font-medium text-white/80 flex items-center gap-2 bg-foreground/50 w-max px-4 py-2 rounded-full backdrop-blur-md">
                    <Link to="/" className="hover:text-white transition-colors">{t('common.home')}</Link>
                    <span>/</span>
                    <Link to="/projekte" className="hover:text-white transition-colors">{t('projects.breadcrumb')}</Link>
                    <span>/</span>
                    <span className="text-white">{project.title}</span>
                </nav>

                <div className="bg-white rounded-3xl shadow-sm border border-border p-8 sm:p-12 text-lg leading-relaxed text-foreground/80 whitespace-pre-line">
                    <button
                        onClick={() => navigate('/projekte')}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors mb-8"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {t('events.back_to_overview')}
                    </button>

                    <div>
                        {project.description}
                    </div>
                </div>
            </div>
        </div>
    );
};
